import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FormFooter } from "@/components/ui/form-footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import type { SandboxProviderCatalogItem } from "@/lib/api/sandboxes";
import { sandboxProviderLabel } from "@/lib/connectors/sandbox-rows";

/**
 * Create a sandbox for one locked provider kind. The kind is chosen upstream
 * (catalog card / deep link) — this form never re-asks "which provider?".
 */
export function CreateSandboxForm({
	providerKind,
	credentialFields,
	pending,
	onCancel,
	onCreate,
}: {
	/** Gateway runtime provider id (e.g. "vercel"). */
	providerKind: string;
	credentialFields: SandboxProviderCatalogItem["credentialFields"];
	pending: boolean;
	onCancel: () => void;
	onCreate: (payload: {
		name: string;
		provider_kind: string;
		credential?: Record<string, string>;
	}) => void;
}) {
	const [name, setName] = useState("");
	const [credential, setCredential] = useState<Record<string, string>>({});
	const label = sandboxProviderLabel(providerKind);

	const submit = (event: React.FormEvent) => {
		event.preventDefault();
		const filled = Object.fromEntries(
			Object.entries(credential).filter(([, v]) => v.trim()),
		);
		onCreate({
			name: name.trim(),
			provider_kind: providerKind,
			credential: Object.keys(filled).length > 0 ? filled : undefined,
		});
	};

	return (
		<div className="space-y-3">
			<div className="space-y-0.5">
				<h3 className="text-sm font-medium">Connect {label}</h3>
				<p className="text-xs text-muted-foreground">
					Add a credential so agents can execute in a {label} sandbox.
				</p>
			</div>
			<form onSubmit={submit} className="grid max-w-md gap-3">
				<div className="grid gap-2">
					<Label htmlFor="sandbox-name">
						Name <span className="ml-1 text-destructive">*</span>
					</Label>
					<Input
						id="sandbox-name"
						placeholder={`${label} production`}
						value={name}
						onChange={(e) => setName(e.target.value)}
						required
					/>
				</div>
				{credentialFields.map((f) => (
					<div key={f.key} className="grid gap-2">
						<Label htmlFor={`sandbox-cred-${f.key}`}>
							{f.label}
							{f.required ? (
								<span className="ml-1 text-destructive">*</span>
							) : null}
						</Label>
						<Input
							id={`sandbox-cred-${f.key}`}
							type={f.secret === false ? "text" : "password"}
							value={credential[f.key] ?? ""}
							onChange={(e) =>
								setCredential((c) => ({ ...c, [f.key]: e.target.value }))
							}
							autoComplete="off"
							required={f.required}
						/>
					</div>
				))}
				<FormFooter onCancel={onCancel}>
					<Button type="submit" disabled={pending || !name.trim()}>
						{pending ? (
							<>
								<Spinner size="lg" className="mr-2" />
								Connecting...
							</>
						) : (
							`Connect ${label}`
						)}
					</Button>
				</FormFooter>
			</form>
		</div>
	);
}
